<template>
  <v-row no-gutters class="pt-5 pr-2">
    <v-col v-if="isFetched">
      <card-title-row translation-path="sidebar.billingAnomalyDetection" :has-buttons="false" />
      <card-title-row is-subtitle :straight-title="title" :has-buttons="false" class="pt-5 pb-4" />
      <sidebar-ranges-style-rows
        ref="billingAnomalyDetectionFilter"
        :is-valid.sync="rangesValid"
        :filter-attribute="dayAverageUsageAttributeName"
        v-model="ranges"
      />
      <sidebar-row>
        <v-col cols="12" class="pa-0 pr-10">
          <data-input v-bind="sliderInput" v-model="sliderValue" />
        </v-col>
      </sidebar-row>
    </v-col>
    <main-loader v-else />
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';
import { debounce } from 'lodash';
import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader.vue';
import SidebarRangesStyleRows from '@/components/SidebarRangesStyleRows.vue';
import SidebarRow from '@/components/SidebarRow.vue';

export default {
  name: 'BillingAnomalyDetection',
  components: {
    CardTitleRow,
    DataInput,
    MainLoader,
    SidebarRangesStyleRows,
    SidebarRow,
  },
  computed: {
    dayAverageUsageAttributeName: get(
      'admin/modulesMapping@billing.watermeters_datasource_attributes_mapping.day_average_usage_attribute_name'
    ),
    minMaxWaterConsumptionValues: get('billings/minMaxWaterConsumptionValues'),
    watermetersLayerId: get('admin/modulesMapping@billing.layer_id'),
    min() {
      return this.minMaxWaterConsumptionValues.min;
    },
    max() {
      return this.minMaxWaterConsumptionValues.max;
    },
    sliderInput() {
      return {
        color: this.ranges[0].color,
        countingRight: this.rangesCount.right,
        countingLeft: this.rangesCount.left,
        dataType: { name: 'slider' },
        hasCountingChips: this.countingChipsVisible,
        hasTumbLabel: true,
        hideDetails: true,
        hideValueLabel: true,
        max: this.max,
        min: this.min,
        step: 0.000001,
        thumbColor: this.ranges[0].color,
        trackColor: this.ranges[1].color,
      };
    },
    title() {
      const m3 = new DOMParser().parseFromString('m&sup3', 'text/html').body.textContent;
      return `${this.$i18n.t('sidebar.dialyAverageWaterConsumption')} (${m3})`;
    },
  },
  data: () => ({
    countingChipsVisible: false,
    isFetched: false,
    ranges: [
      { color: 'green', size: '5', value: true, minValue: null, maxValue: null },
      { color: 'red', size: '5', value: true, minValue: null, maxValue: null },
    ],
    rangesCount: {
      left: 0,
      right: 0,
    },
    rangesValid: false,
    sliderValue: 0,
  }),
  methods: {
    getLayerFeatures: call('layers/getLayerFeatures'),
    getMinMaxWaterConsumptionValues: call('billings/getMinMaxWaterConsumptionValues'),
    async getRangesCount() {
      const [rLeft, rRight] = await Promise.all([
        this.getLayerFeatures(this.getRangesCountPayload('left')),
        this.getLayerFeatures(this.getRangesCountPayload('right')),
      ]);
      this.rangesCount.left = rLeft.data.data.features_count;
      this.rangesCount.right = rRight.data.data.features_count;
      this.countingChipsVisible = true;
    },
    getRangesCountPayload(side) {
      const operator = side === 'left' ? '$LESS_OR_EQUAL' : '$GREATER_THAN';
      const features_filter = {
        [operator]: {
          [this.dayAverageUsageAttributeName]: { value: this.sliderValue },
        },
      };
      const payload = {
        layer_id: this.watermetersLayerId,
        features_filter,
        params: {
          with_features: false,
          with_geometry: false,
        },
      };
      return payload;
    },
    updateStyle(ranges) {
      if (this.rangesValid) {
        localStorage.setItem('billing-anomaly-ranges', JSON.stringify(ranges));
        this.$root.$emit('toggleBillingAnomalyVisualisation', { value: true, ranges: this.ranges });
      }
    },
    watermetersFetched() {
      this.$root.$emit('toggleBillingAnomalyVisualisation', { value: true, ranges: this.ranges });
      this.$refs.billingAnomalyDetectionFilter?.filter();
    },
    async init() {
      try {
        await this.getMinMaxWaterConsumptionValues();
        const billingAnomalyRanges = localStorage.getItem('billing-anomaly-ranges');
        if (billingAnomalyRanges) {
          this.ranges = JSON.parse(billingAnomalyRanges);
          const storageOne = this.ranges[0].maxValue;
          const storageTwo = this.ranges[1].minValue;
          if (
            storageOne &&
            storageTwo &&
            storageOne === storageTwo &&
            storageOne >= this.min &&
            storageOne <= this.max
          ) {
            this.sliderValue = parseFloat(storageOne.toFixed(2));
          } else {
            this.sliderValue = parseFloat(((this.min + this.max) / 2).toFixed(2));
          }
        } else {
          this.sliderValue = parseFloat(((this.min + this.max) / 2).toFixed(2));
        }
        this.ranges[0].minValue = this.min;
        this.ranges[1].maxValue = this.max;
        this.isFetched = true;
        this.$root.$emit('toggleBillingAnomalyVisualisation', { value: true, ranges: this.ranges });
        setTimeout(() => {
          this.$refs.billingAnomalyDetectionFilter?.filter();
        });
      } catch {
        this.isFetched = true;
      }
    },
  },
  watch: {
    ranges: {
      deep: true,
      immediate: true,
      handler(nV) {
        this.updateStyle(nV);
      },
    },
    sliderValue(nV) {
      this.ranges[0].maxValue = nV;
      this.ranges[1].minValue = nV;
      this.getRangesCount();
    },
  },
  created() {
    this.updateStyle = debounce(this.updateStyle, 500);
    this.getRangesCount = debounce(this.getRangesCount, 1000);
  },
  mounted() {
    this.$root.$emit('setSidebarFilters', {}, false);
    this.$root.$on('watermetersFetched', this.watermetersFetched);
    this.init();
  },
  beforeDestroy() {
    this.$root.$off('watermetersFetched');
  },
};
</script>
